// Plan-capacity guards for checkbox groups.
//
// Several pickers in the app let the operator select from a list that their
// plan caps: accommodations on a website, languages on a site. Before this
// module the caps were enforced only on the server, so the operator could tick
// eight boxes and learn on "Continue" that two was the limit — after the click,
// with no indication of which selections were the problem.
//
// The rule here is: make exceeding the cap unreachable in the UI rather than
// rejecting it afterwards. Already-checked boxes stay enabled so a selection
// can always be swapped; only the un-chosen ones lock.
//
// The server checks still run and are still authoritative — this is the
// affordance, not the enforcement.

export type CapacityState = {
  /** How many boxes are currently checked. */
  used: number;
  /** True when no further box may be checked. */
  full: boolean;
};

/**
 * Recomputes a checkbox group's capacity state and locks the un-chosen boxes
 * when the cap is reached.
 *
 * `limit` is the number of boxes that may be checked at once; a negative limit
 * means unlimited (matching domain.Entitlements, where -1 is unlimited) and
 * never disables anything.
 *
 * Disabling is applied imperatively rather than through per-row `:disabled`
 * bindings because these lists are re-rendered by htmx swaps — a binding
 * would have to be re-established per row, while one pass over the live DOM
 * after each change and on init covers every render path.
 */
export function applyCapacity(boxes: HTMLInputElement[], limit: number): CapacityState {
  const used = boxes.reduce((n, b) => n + (b.checked ? 1 : 0), 0);
  const full = limit >= 0 && used >= limit;
  for (const b of boxes) {
    // Never disable a checked box: the operator must always be able to give
    // capacity back, otherwise a full selection is a dead end.
    b.disabled = full && !b.checked;
  }
  return { used, full };
}

/** Collects the live checkbox elements of `name` inside `root`. */
export function capacityBoxes(root: HTMLElement, name: string): HTMLInputElement[] {
  return Array.from(
    root.querySelectorAll<HTMLInputElement>(`input[type="checkbox"][name="${name}"]`),
  );
}
